import {
  IGetAllBedsHiistorySingleResponse,
  IGetAllCampsSingleResponse,
} from 'types/api/accommodationApi.interface'
import {
  IAccommodationOccupant,
  IAccommodationStateCounts,
  IComputedBed,
  IComputedBlock,
  IComputedCamp,
  IComputedRoom,
  IFlatBed,
} from 'types/accommodation.interface'

export const BED_AVAILABLE = 1
export const BED_OCCUPIED = 2
export const BED_DIRTY = 3
export const BED_BLOCKED = 4

export const STATUS_INACTIVE = 2

const isInactive = (status: number) => status === STATUS_INACTIVE

export const occupantName = (o: IGetAllBedsHiistorySingleResponse) => {
  if (o.user) return `${o.user.firstName} ${o.user.lastName}`
  if (o.accommodationGuest) return `${o.accommodationGuest.firstName} ${o.accommodationGuest.lastName}`
  return 'Unknown'
}

export const buildOccupantMap = (
  history: IGetAllBedsHiistorySingleResponse[],
): Map<number, IAccommodationOccupant> => {
  const map = new Map<number, IAccommodationOccupant>()
  history.forEach((o) => {
    if (o.actualCheckOutTimestamp) return
    if (!o.bed?.id) return
    map.set(o.bed.id, {
      assignmentId: o.id,
      name: occupantName(o),
      personType: o.personType,
      checkIn: o.checkInTimestamp,
      expectedCheckOut: o.expectedCheckOutTimestamp,
      overdue:
        !!o.expectedCheckOutTimestamp && new Date(o.expectedCheckOutTimestamp).getTime() < Date.now(),
    })
  })
  return map
}

export const effectiveBedState = (bedState: number, outOfService: boolean, hasOccupant: boolean) => {
  if (hasOccupant) return BED_OCCUPIED
  if (outOfService || bedState === BED_BLOCKED) return BED_BLOCKED
  if (bedState === BED_DIRTY) return BED_DIRTY
  return BED_AVAILABLE
}

const emptyCounts = (): IAccommodationStateCounts => ({
  total: 0,
  available: 0,
  occupied: 0,
  dirty: 0,
  blocked: 0,
  occupancy: 0,
})

const addBed = (counts: IAccommodationStateCounts, effectiveState: number) => {
  counts.total += 1
  if (effectiveState === BED_OCCUPIED) counts.occupied += 1
  else if (effectiveState === BED_DIRTY) counts.dirty += 1
  else if (effectiveState === BED_BLOCKED) counts.blocked += 1
  else counts.available += 1
}

const withOccupancy = (counts: IAccommodationStateCounts): IAccommodationStateCounts => ({
  ...counts,
  occupancy: counts.total ? Math.round((counts.occupied / counts.total) * 100) : 0,
})

export const computeCamp = (
  camp: IGetAllCampsSingleResponse,
  occupantByBed: Map<number, IAccommodationOccupant>,
): IComputedCamp => {
  const campCounts = emptyCounts()

  const campInactive = isInactive(camp.status)

  const blocks: IComputedBlock[] = (camp.blocks || []).map((block) => {
    const blockCounts = emptyCounts()
    const blockInactive = campInactive || isInactive(block.status)

    const rooms: IComputedRoom[] = (block.rooms || []).map((room) => {
      const roomCounts = emptyCounts()
      const outOfService = blockInactive || isInactive(room.status)

      const beds: IComputedBed[] = (room.beds || []).map((bed) => {
        const occupant = occupantByBed.get(bed.id) || null
        const effectiveState = effectiveBedState(bed.state, outOfService, !!occupant)
        addBed(roomCounts, effectiveState)
        addBed(blockCounts, effectiveState)
        addBed(campCounts, effectiveState)
        return {
          id: bed.id,
          label: bed.label,
          rawState: bed.state,
          effectiveState,
          occupant,
        }
      })

      return {
        id: room.id,
        name: room.label,
        campName: camp.name,
        blockName: block.name,
        gender: block.genderPolicy,
        tier: block.tierPolicy,
        status: room.status,
        outOfService,
        beds,
        counts: withOccupancy(roomCounts),
      }
    })

    return {
      id: block.id,
      name: block.name,
      rooms,
      counts: withOccupancy(blockCounts),
    }
  })

  return {
    id: camp.id,
    name: camp.name,
    blocksCount: camp.blocks?.length || 0,
    blocks,
    counts: withOccupancy(campCounts),
  }
}

export const computeCamps = (
  camps: IGetAllCampsSingleResponse[],
  occupantByBed: Map<number, IAccommodationOccupant>,
): IComputedCamp[] => camps.map((camp) => computeCamp(camp, occupantByBed))

export const aggregateCounts = (computed: IComputedCamp[]): IAccommodationStateCounts => {
  const total = emptyCounts()
  computed.forEach((camp) => {
    total.total += camp.counts.total
    total.available += camp.counts.available
    total.occupied += camp.counts.occupied
    total.dirty += camp.counts.dirty
    total.blocked += camp.counts.blocked
  })
  return withOccupancy(total)
}

export const flattenBeds = (computed: IComputedCamp[]): IFlatBed[] => {
  const flat: IFlatBed[] = []
  computed.forEach((camp) => {
    camp.blocks.forEach((block) => {
      block.rooms.forEach((room) => {
        room.beds.forEach((bed) => {
          flat.push({
            bedId: bed.id,
            label: bed.label,
            effectiveState: bed.effectiveState,
            roomId: room.id,
            outOfService: room.outOfService,
            campName: camp.name,
            blockName: block.name,
            roomName: room.name,
            occupant: bed.occupant,
          })
        })
      })
    })
  })
  return flat
}
